import {
  ChangeHistoryEntry,
  fromChangesHistory,
} from "components/change-history-panel/change-history-entry";
import { pageEntries } from "components/change-history-panel/change-history-panel";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { PopoverController } from "ionic-angular";
import moment from "moment";
import { PrnCommentPopover } from "pages/assessment/prn-comment-popover/prn-comment-popover";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import {
  authorizationTypeList,
  Config,
  PatientBenefitActionEvent,
  PatientBenefits,
  relationshipList,
} from "../models";

@Component({
  selector: "patient-benefits-row",
  templateUrl: "patient-benefits-row.html",
})
export class PatientBenefitsRowComponent implements OnInit {
  @Input() benefit: PatientBenefits;
  @Input() i: number;
  @Input() canEdit: boolean;

  // CO-2315 Round-2 item #11 / Hospice-as-Primary F1.S13 - the election and attestation
  // audit, read back beside the values it audits.
  //
  // The trail arrives on the benefit already: patient-benefits-get-benefits-by-patient
  // returns the whole item, changesHistory included, and nothing rendered it until now
  // (`grep -c changesHistory clinical-ui/src` was 0 on 2026-09-06).
  //
  // Only the two election fields belong here. changesHistory is a SHARED list that also
  // carries payer and hold entries; putting a payer change under Hospice Eligibility
  // Details would break F1.S3's "on this benefit's own details" rule.
  static readonly ELECTION_AUDIT_FIELDS = [
    "hospiceBenefitElectionDate",
    "noticeAcceptedDate",
  ];
  electionHistoryPageSize: number = 5;
  currentElectionHistoryPage: number = 0;
  electionHistoryPageCount: number = 0;
  pagedElectionHistory: ChangeHistoryEntry[] = [];
  private electionHistoryPages: ChangeHistoryEntry[][] = [];

  // A property, not a method, so the panel's @Input identity is stable across change
  // detection - this component renders once per benefit row.
  onElectionHistoryPageChange = (page: number): void => {
    this.currentElectionHistoryPage = page;
    this.pagedElectionHistory = this.electionHistoryPages[page] || [];
  };

  // electionAuditEntries filters the shared trail to the two election fields and
  // normalises it for the shared panel.
  electionAuditEntries(): ChangeHistoryEntry[] {
    const raw =
      this.benefit && this.benefit["changesHistory"]
        ? this.benefit["changesHistory"]
        : [];
    const elections = (raw || []).filter(
      (h) =>
        h &&
        PatientBenefitsRowComponent.ELECTION_AUDIT_FIELDS.indexOf(
          h.changesMadeTo
        ) !== -1
    );
    return fromChangesHistory(elections);
  }

  buildElectionHistory(): void {
    this.currentElectionHistoryPage = 0;
    this.electionHistoryPages = pageEntries(
      this.electionAuditEntries(),
      this.electionHistoryPageSize
    );
    this.electionHistoryPageCount = this.electionHistoryPages.length;
    this.pagedElectionHistory = this.electionHistoryPages[0] || [];
  }
  @Output() editPatientBenefitEvent =
    new EventEmitter<PatientBenefitActionEvent>();

  planAddress: string;
  patientStatus: string;

  constructor(
    private popover: PopoverController,
    private referralsService: ReferralsServiceProvider
  ) {}

  // payerSubtypeList = payerSubtypeList;
  relationshipList = relationshipList;
  authorizationTypeList = authorizationTypeList;

  // payerSubtypeSelected: Config;
  relationshipSelected: Config;

  ngOnInit(): void {
    this.buildElectionHistory();
    // this.payerSubtypeSelected = this.payerSubtypeList.find(f => f.value === this.benefit.payerSubtype);
    this.planAddress = this.getPlanAddress();
    this.relationshipSelected = this.relationshipList.find(
      (f) => f.value === this.benefit.subscriber.relationship
    );
    this.patientStatus = String(
      this.referralsService.PATIENT_STATUS
    ).toUpperCase();
  }

  getPlanAddress(): string {
    if (
      this.benefit.plan.planName === "" ||
      this.benefit.plan.planName === "-"
    ) {
      return "-";
    } else {
      let plan = this.benefit.plan;
      // plan.address2 = plan.address2 === '-' ? '' : plan.address2;
      let zipExt =
        plan.zipCodeExtension === "" || plan.zipCodeExtension === "-"
          ? ""
          : " - " + plan.zipCodeExtension;

      return (
        plan.address1 +
        ", " +
        plan.city +
        ", " +
        plan.state +
        " " +
        plan.zipCode +
        zipExt
      );
    }
  }

  showDetails(benefit: PatientBenefits) {
    benefit.showDetails = !benefit.showDetails;
  }

  showMoreOptions(ev, benefit: PatientBenefits) {
    const options = this.popover.create(
      "PatientBenefitsOptionsComponent",
      { canEdit: this.canEdit, patientBenefit: benefit },
      { cssClass: "patient-benefits-options" }
    );
    options.onDidDismiss((action) => {
      const patientBenefitAction = { patientBenefit: benefit, action: action };
      this.editPatientBenefitEvent.emit(patientBenefitAction);
    });
    options.present({ ev });
  }

  getAuthorizationType(value: String) {
    if (value !== null) {
      // CO-3068 — find() returns undefined when the stored value is not in the
      // (mostly commented-out) authorizationTypeList; `.name` on undefined
      // threw a TypeError that blew up the whole row render. Guard the lookup.
      const match = this.authorizationTypeList.find((f) => f.value === value);
      return match ? match.name : "-";
    } else {
      return "-";
    }
  }

  onShowModal(event: any, benefit: PatientBenefits) {
    event.stopPropagation();
    let popover = this.popover.create(
      PrnCommentPopover,
      {
        comment: benefit.reasonForHold,
      },
      { cssClass: "toolTip toolTip-benefit-row" }
    );
    popover.present({
      ev: event,
    });
  }

  getCurrentSubscriberId(): string {
    let subscriberId = this.benefit.subscriber.subscriberId;
    if (this.benefit.subscriber.subscriberHistory) {
      let currentDate = new Date();
      currentDate.setHours(0, 0, 0, 0);
      this.benefit.subscriber.subscriberHistory.forEach((s) => {
        if (
          moment(currentDate).isSameOrAfter(s.effectiveDate) &&
          (s.expiredDate == "" ||
            moment(currentDate).isSameOrBefore(s.expiredDate))
        ) {
          subscriberId = s.subscriberId;
        }
      });
    }
    return subscriberId;
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-benefits/patient-benefits-row/patient-benefits-row.ts